import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams, Link } from 'react-router-dom';
import { Card, Button } from '../components';
import { FileText, ArrowLeft, Calendar, User, Download } from 'lucide-react';
import api from '../services/api';
import { getDossierDetail } from '../services/employerTrajectenApi';
import { useAuthStore } from '../store/authStore';
import { useTranslation } from 'react-i18next';

export const DossierVerslagenPage: React.FC = () => {
  const { dossierId } = useParams<{ dossierId: string }>();
  const { user } = useAuthStore();
  const { t } = useTranslation('professional');

  const apiPath = user?.role === 'professional'
    ? `/professional/dossiers/${dossierId}/verslagen`
    : `/employer/dossiers/${dossierId}/verslagen`;

  const { data: verslagen, isLoading } = useQuery({
    queryKey: ['dossier-verslagen', dossierId],
    queryFn: async () => {
      const response = await api.get(apiPath);
      return response.data.data || [];
    },
    enabled: !!dossierId,
  });

  // Besluit 1.1: de werkgever ziet de cliëntnaam alleen hier, in het detailscherm van het dossier.
  const { data: dossierDetail } = useQuery({
    queryKey: ['employer-dossier-detail', dossierId],
    queryFn: () => getDossierDetail(dossierId!),
    enabled: !!dossierId && user?.role !== 'professional',
  });

  const handleDownloadPdf = (verslagId: string, _title: string) => {
    const pdfPath = user?.role === 'professional'
      ? `/api/professional/dossiers/${dossierId}/verslagen/${verslagId}/pdf`
      : `/api/employer/dossiers/${dossierId}/verslagen/${verslagId}/pdf`;

    const timestamp = Date.now();
    const url = `${pdfPath}?t=${timestamp}`;
    window.open(url, '_blank');
  };

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center py-12">
          <p className="text-gray-600">{t('verslagen.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <Link to={user?.role === 'professional' ? '/professional/dossiers' : '/employer/dossiers'}>
          <Button variant="outline" className="mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('verslagen.backToDossiers')}
          </Button>
        </Link>
        <h1 className="text-2xl font-bold text-gray-900">{t('verslagen.title')}</h1>
        <p className="mt-1 text-gray-600">{t('verslagen.subtitle')}</p>
        {dossierDetail && (
          <p className="mt-2 text-sm text-gray-700">
            <span className="font-mono">{dossierDetail.registratienummer}</span>
            {' · '}
            <strong>{t('verslagen.medewerker')}:</strong> {dossierDetail.client_name}
          </p>
        )}
      </div>

      {!verslagen || verslagen.length === 0 ? (
        <Card>
          <div className="text-center py-12">
            <FileText className="mx-auto h-16 w-16 text-gray-400 mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              {t('verslagen.noReportsTitle')}
            </h2>
            <p className="text-gray-600">
              {t('verslagen.noReportsDesc')}
            </p>
          </div>
        </Card>
      ) : (
        <div className="space-y-4">
          {verslagen.map((verslag: any) => (
            <Card key={verslag.id}>
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 mb-2">{verslag.title}</h3>
                    <div className="flex items-center space-x-4 text-sm text-gray-500">
                      <div className="flex items-center">
                        <Calendar className="w-4 h-4 mr-1" />
                        {new Date(verslag.verslag_datum).toLocaleDateString('nl-NL')}
                      </div>
                      <div className="flex items-center">
                        <User className="w-4 h-4 mr-1" />
                        {verslag.professional_first_name} {verslag.professional_last_name}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      verslag.type === 'intake' ? 'bg-blue-100 text-blue-800'
                      : verslag.type === 'session' ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-800'
                    }`}>
                      {verslag.type}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDownloadPdf(verslag.id, verslag.title)}
                      className="flex items-center"
                    >
                      <Download className="w-4 h-4 mr-1" />
                      {t('verslagen.downloadPdf')}
                    </Button>
                  </div>
                </div>
                <div className="mb-4 space-y-2 text-sm">
                  {verslag.session_number && (
                    <div className="text-gray-600">
                      <strong>{t('verslagen.sessionNumber')}:</strong> {verslag.session_number}
                    </div>
                  )}
                  {verslag.report_status && (
                    <div className="text-gray-600">
                      <strong>{t('verslagen.status')}:</strong> {verslag.report_status === 'vertrouwelijk'
                        ? t('verslagen.statusConfidential')
                        : t('verslagen.statusReport')}
                    </div>
                  )}
                </div>

                {/* Show betrokkenen if available */}
                {verslag.extended_data && (() => {
                  try {
                    const extendedData = typeof verslag.extended_data === 'string'
                      ? JSON.parse(verslag.extended_data)
                      : verslag.extended_data;

                    const hasBetrokkenen = extendedData.employer_name || extendedData.client_name || extendedData.professional_name;

                    if (hasBetrokkenen) {
                      return (
                        <div className="mb-4 pt-4 border-t">
                          <h4 className="font-medium text-gray-900 mb-3">{t('verslagen.betrokkenen')}</h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                            {extendedData.employer_name && (
                              <div>
                                <strong>{t('verslagen.werkgever')}:</strong> {extendedData.employer_name}
                                {extendedData.employer_email && <div className="text-gray-600">{extendedData.employer_email}</div>}
                              </div>
                            )}
                            {extendedData.client_name && (
                              <div>
                                <strong>{t('verslagen.medewerker')}:</strong> {extendedData.client_name}
                                {extendedData.client_email && <div className="text-gray-600">{extendedData.client_email}</div>}
                                {extendedData.client_birthdate && (
                                  <div className="text-gray-600">{t('verslagen.geboortedatum')}: {new Date(extendedData.client_birthdate).toLocaleDateString('nl-NL')}</div>
                                )}
                              </div>
                            )}
                            {extendedData.contact_person_name && (
                              <div>
                                <strong>{t('verslagen.contactpersoon')}:</strong> {extendedData.contact_person_name}
                                {extendedData.contact_person_email && <div className="text-gray-600">{extendedData.contact_person_email}</div>}
                              </div>
                            )}
                            {extendedData.manager_name && (
                              <div>
                                <strong>{t('verslagen.leidinggevende')}:</strong> {extendedData.manager_name}
                                {extendedData.manager_email && <div className="text-gray-600">{extendedData.manager_email}</div>}
                              </div>
                            )}
                            {extendedData.company_doctor_name && (
                              <div>
                                <strong>{t('verslagen.bedrijfsarts')}:</strong> {extendedData.company_doctor_name}
                                {extendedData.company_doctor_email && <div className="text-gray-600">{extendedData.company_doctor_email}</div>}
                              </div>
                            )}
                            {extendedData.professional_name && (
                              <div>
                                <strong>{t('verslagen.bmwZzp')}:</strong> {extendedData.professional_name}
                                {extendedData.professional_email && <div className="text-gray-600">{extendedData.professional_email}</div>}
                                {extendedData.professional_phone && <div className="text-gray-600">{extendedData.professional_phone}</div>}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    }
                  } catch { /* ignore */ }
                  return null;
                })()}

                {/* Show extended data if available */}
                {verslag.extended_data && (() => {
                  try {
                    const extendedData = typeof verslag.extended_data === 'string'
                      ? JSON.parse(verslag.extended_data)
                      : verslag.extended_data;

                    const optionalFields = [
                      { key: 'hulpvraag_medewerker', label: t('verslagen.fields.hulpvraag_medewerker') },
                      { key: 'reden_begeleiding', label: t('verslagen.fields.reden_begeleiding') },
                      { key: 'werksituatie', label: t('verslagen.fields.werksituatie') },
                      { key: 'persoonlijke_situatie', label: t('verslagen.fields.persoonlijke_situatie') },
                      { key: 'inzicht', label: t('verslagen.fields.inzicht') },
                      { key: 'acties_medewerker', label: t('verslagen.fields.acties_medewerker') },
                      { key: 'rol_professioneel', label: t('verslagen.fields.rol_professioneel') },
                      { key: 'advies_werkgever', label: t('verslagen.fields.advies_werkgever') },
                      { key: 'verwacht_resultaat', label: t('verslagen.fields.verwacht_resultaat') },
                      { key: 'vervolg', label: t('verslagen.fields.vervolg') },
                    ];

                    const enabledFields = optionalFields.filter(f =>
                      extendedData[f.key]?.enabled && extendedData[f.key]?.text
                    );

                    if (enabledFields.length > 0) {
                      return (
                        <div className="mt-4 pt-4 border-t space-y-3">
                          {enabledFields.map(field => (
                            <div key={field.key}>
                              <h4 className="font-medium text-gray-900 mb-1">{field.label}</h4>
                              <p className="text-gray-700 whitespace-pre-wrap text-sm">
                                {extendedData[field.key]?.text}
                              </p>
                            </div>
                          ))}
                        </div>
                      );
                    }
                    return null;
                  } catch {
                    return null;
                  }
                })()}

                {/* Fallback to old content format if extended_data is not available */}
                {(!verslag.extended_data || (typeof verslag.extended_data === 'string' && verslag.extended_data === '{}')) && verslag.content && (
                  <div className="prose max-w-none mt-4">
                    <p className="text-gray-700 whitespace-pre-wrap">{verslag.content}</p>
                  </div>
                )}

                {verslag.action_points && (() => {
                  try {
                    const actionPoints = typeof verslag.action_points === 'string'
                      ? JSON.parse(verslag.action_points)
                      : verslag.action_points;
                    if (actionPoints && actionPoints.length > 0) {
                      return (
                        <div className="mt-4 pt-4 border-t">
                          <h4 className="font-medium text-gray-900 mb-2">{t('verslagen.actionPoints')}:</h4>
                          <ul className="list-disc list-inside space-y-1 text-sm text-gray-600">
                            {actionPoints.map((point: string, idx: number) => (
                              <li key={idx}>{point}</li>
                            ))}
                          </ul>
                        </div>
                      );
                    }
                  } catch { /* ignore */ }
                  return null;
                })()}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};


